import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';
import { PaginatedLeads } from './workspace-leads.service';

/**
 * "Proceed to Leads" from Advanced Prospecting — an unsaved prospecting list
 * (identified by a browser-generated UUID) whose leads are generated from the
 * Veridion search filters + the chosen job titles / departments.
 * See backend docs/prospecting/api-contract.md.
 */

/** Where the browser keeps a list's configuration (per tab). */
export const PROSPECTING_LIST_STORAGE_PREFIX = 'salesommo_prospecting_list:';

export interface ProspectingListConfig {
  filters: any[];
  job_titles: string[];
  departments: string[];
  summary_label: string;
  total_companies: number;
}

export interface ProspectingMapping {
  mapped: {
    countries?: string[];
    industries?: string[];
    company_sizes?: string[];
    company_revenues?: string[];
    business_categories?: string[];
    company_products?: string[];
    [key: string]: any;
  };
  list_only: { attribute: string; label: string; value: string }[];
  warnings: string[];
}

export interface ProspectingPricing {
  display_max: number;
  manual: { [count: string]: number };
  per_lead: number;
  surcharge_per_10: number;
  email_per_found: number;
  max_export_leads: number;
  export_block_size: number;
}

export interface ProspectingPreview {
  mapping: ProspectingMapping;
  available_companies: number | null;
  companies_walked: number | null;
  companies_left: number | null;
  pricing: ProspectingPricing;
}

export interface ProspectingListSummary {
  list_id: string;
  summary_label: string;
  total_companies: number;
  job_titles: string[];
  departments: string[];
  leads_count: number;
  unlocked_emails: number;
  next_expiry_at: string | null;
  companies_walked: number;
  list_exhausted: boolean;
  active_run_id: number | null;
  active_export_id: number | null;
  exports_count: number;
  saved_icp_id: number | null;
  last_activity_at: string | null;
}

export interface ProspectingListDetail extends ProspectingListSummary {
  config: ProspectingListConfig;
  mapping?: ProspectingMapping;
}

export interface ProspectingGenerateResult {
  run_id: number;
  prospecting_list_id: string;
  leads_requested: number;
  credits_required: number;
  status: string;
  mapping?: ProspectingMapping;
}

export type LeadExportStatus = 'pending' | 'processing' | 'completed' | 'completed_partial' | 'failed' | 'cancelled';

export interface LeadExport {
  id: number;
  prospecting_list_id: string | null;
  summary_label: string | null;
  status: LeadExportStatus;
  stop_reason: null | 'list_exhausted' | 'insufficient_credits' | 'cancelled' | 'limit' | 'error';
  requested_mode: 'custom' | 'all';
  requested_count: number | null;
  include_emails: boolean;
  columns?: string[];
  /** 1 for a new export, 2+ for continuations of an export that ran out of credits. */
  part?: number;
  continued_from_id?: number | null;
  /** Leads delivered by the earlier parts. */
  lead_offset?: number;
  /** Leads still wanted (custom target); null for "all". */
  remaining_count?: number | null;
  can_continue?: boolean;
  continued_by_id?: number | null;
  pool_total: number | null;
  companies_processed: number;
  leads_found: number;
  emails_found: number;
  credits_used: number;
  progress_percentage: number;
  is_downloadable: boolean;
  file_name: string | null;
  file_size: number | null;
  file_size_formatted: string | null;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface LeadExportPage {
  data: LeadExport[];
  metadata: { total?: number; per_page?: number; current_page?: number; last_page?: number };
}

export interface UnlockSummary {
  total: number;
  unlocked: number;
  locked: number;
  not_found: number;
  cost_per_email: number;
  max_cost: number;
  balance: number;
  affordable: number;
}

export interface UnlockJob {
  job_id: string | number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  total: number;
  processed: number;
  found: number;
  not_found: number;
  credits_used: number;
  stop_reason: null | 'insufficient_credits' | 'error';
}

export interface SaveListResult {
  icp: any;
  missing_required: string[];
  created: boolean;
}

/** Scope of a CSV export / bulk unlock: a prospecting list or an ICP. */
export type LeadScope = { prospecting_list_id: string } | { icp_id: number };

/** Default prices (the preview's pricing block overrides them when loaded). */
export const DEFAULT_MANUAL_PRICES: { [count: number]: number } = { 5: 16, 10: 26 };
export const MAX_EXPORT_LEADS = 2000;
export const EMAIL_CREDITS_PER_FOUND = 2;

/** Export credits for d > 10 leads: 26 + 2·(d−10) + floor((d−10)/10). */
export function exportLeadsCost(count: number): number {
  const d = Math.max(0, Math.floor(count));
  if (d === 0) {
    return 0;
  }
  if (d <= 10) {
    // 5-lead price ± 2 per lead (same as the backend's ProspectingPricing).
    return Math.max(0, DEFAULT_MANUAL_PRICES[5] + 2 * (d - 5));
  }
  return DEFAULT_MANUAL_PRICES[10] + 2 * (d - 10) + Math.floor((d - 10) / 10);
}

/**
 * Price of `count` more leads after `offset` already delivered by earlier
 * parts of the same export (a continued export costs the rest of one
 * uninterrupted export).
 */
export function exportLeadsCostFrom(offset: number, count: number): number {
  const o = Math.max(0, Math.floor(offset));
  return o === 0
    ? exportLeadsCost(count)
    : Math.max(0, exportLeadsCost(o + Math.max(0, Math.floor(count))) - exportLeadsCost(o));
}

/** How many more leads a continuation may produce (estimate for "all"). */
export function continuationTarget(exp: LeadExport): number {
  const delivered = (exp.lead_offset ?? 0) + exp.leads_found;
  const capLeft = Math.max(0, MAX_EXPORT_LEADS - delivered);
  if (exp.remaining_count != null) {
    return Math.min(exp.remaining_count, capLeft);
  }
  const companiesLeft = exp.pool_total != null ? Math.max(0, exp.pool_total - exp.companies_processed) : capLeft;
  return Math.min(companiesLeft || capLeft, capLeft);
}

/** Human names for ICP fields the backend reports as `missing_required`. */
export const MISSING_REQUIRED_LABELS: { [key: string]: string } = {
  target_company_size_ids: 'company sizes',
  target_industry_ids: 'industries',
  target_company_revenue_ids: 'company revenues',
  what_solutions_do_i_sell: 'solutions you sell',
  challenges_solved: 'challenges solved',
  main_buyer_job_title: 'buyer job titles',
  main_buyer_department: 'buyer departments',
};

export function missingRequiredLabel(keys: string[] | null | undefined): string {
  return (keys ?? []).map(key => MISSING_REQUIRED_LABELS[key] ?? key.replace(/_/g, ' ')).join(', ');
}

export function isLeadExportActive(exp: LeadExport | null | undefined): boolean {
  return !!exp && (exp.status === 'pending' || exp.status === 'processing');
}

/** Save a Blob as a file in the browser. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** Readable message from an API error ({error, message, ...}). */
export function apiErrorMessage(error: any, fallback: string): string {
  return error?.error?.message || (typeof error?.error?.error === 'string' && error.error.error.length > 20 ? error.error.error : '') || fallback;
}

@Injectable({
  providedIn: 'root'
})
export class ProspectingService {

  private apiUrl = environment.api.url;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) {}

  private options(token: string, params?: HttpParams) {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }),
      ...(params ? { params } : {}),
    };
  }

  private scopeParams(scope: LeadScope): HttpParams {
    return 'prospecting_list_id' in scope
      ? new HttpParams().set('prospecting_list_id', scope.prospecting_list_id)
      : new HttpParams().set('icp_id', String(scope.icp_id));
  }

  // ─── Session storage (list config per tab) ─────────────────────────

  static storeConfig(listId: string, config: ProspectingListConfig): void {
    try {
      sessionStorage.setItem(PROSPECTING_LIST_STORAGE_PREFIX + listId, JSON.stringify(config));
    } catch { /* storage unavailable — the server copy is the fallback */ }
  }

  static readConfig(listId: string): ProspectingListConfig | null {
    try {
      const raw = sessionStorage.getItem(PROSPECTING_LIST_STORAGE_PREFIX + listId);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.filters)) {
        return null;
      }
      return {
        filters: parsed.filters,
        job_titles: Array.isArray(parsed.job_titles) ? parsed.job_titles : [],
        departments: Array.isArray(parsed.departments) ? parsed.departments : [],
        summary_label: typeof parsed.summary_label === 'string' ? parsed.summary_label : '',
        total_companies: Number(parsed.total_companies) || 0,
      };
    } catch {
      return null;
    }
  }

  static clearConfig(listId: string): void {
    try {
      sessionStorage.removeItem(PROSPECTING_LIST_STORAGE_PREFIX + listId);
    } catch { /* ignore */ }
  }

  // ─── Prospecting lists ─────────────────────────────────────────────

  /** POST /prospecting/preview — filter → ICP-target mapping + pool + pricing. */
  preview(filters: any[], listId?: string): Observable<ProspectingPreview> {
    const body: { filters: any[]; list_id?: string } = { filters };
    if (listId) {
      body.list_id = listId;
    }
    return this.authService.token().pipe(
      switchMap(token => this.http.post<{ data: ProspectingPreview }>(
        `${this.apiUrl}/prospecting/preview`, body, this.options(token),
      )),
      map(res => res.data),
    );
  }

  /** GET /prospecting/lists */
  getLists(): Observable<ProspectingListSummary[]> {
    return this.authService.token().pipe(
      switchMap(token => this.http.get<{ data: ProspectingListSummary[] }>(
        `${this.apiUrl}/prospecting/lists`, this.options(token),
      )),
      map(res => res.data ?? []),
    );
  }

  /** GET /prospecting/lists/{listId} (404 when unknown/deleted). */
  getList(listId: string): Observable<ProspectingListDetail> {
    return this.authService.token().pipe(
      switchMap(token => this.http.get<{ data: ProspectingListDetail }>(
        `${this.apiUrl}/prospecting/lists/${encodeURIComponent(listId)}`, this.options(token),
      )),
      map(res => res.data),
    );
  }

  /** POST /prospecting/lists/{listId}/generate — 5 or 10 leads. */
  generate(listId: string, config: ProspectingListConfig, count: number): Observable<ProspectingGenerateResult> {
    return this.authService.token().pipe(
      switchMap(token => this.http.post<{ data: ProspectingGenerateResult }>(
        `${this.apiUrl}/prospecting/lists/${encodeURIComponent(listId)}/generate`,
        { ...config, count },
        this.options(token),
      )),
      map(res => res.data),
    );
  }

  /** POST /prospecting/lists/{listId}/export — Excel export of 11–2000 (or all) leads. */
  startExport(
    listId: string,
    config: ProspectingListConfig,
    mode: 'custom' | 'all',
    count: number | null,
    includeEmails: boolean,
    columns?: string[],
  ): Observable<{ export: LeadExport; credits_to_start: number }> {
    const body: any = { ...config, mode, include_emails: includeEmails };
    if (columns?.length) {
      body.columns = columns;
    }
    if (mode === 'custom') {
      body.count = count;
    }
    return this.authService.token().pipe(
      switchMap(token => this.http.post<{ data: { export: LeadExport; credits_to_start: number } }>(
        `${this.apiUrl}/prospecting/lists/${encodeURIComponent(listId)}/export`, body, this.options(token),
      )),
      map(res => res.data),
    );
  }

  /** DELETE /prospecting/lists/{listId} */
  deleteList(listId: string): Observable<{ deleted_leads: number }> {
    return this.authService.token().pipe(
      switchMap(token => this.http.delete<{ data: { deleted_leads: number } }>(
        `${this.apiUrl}/prospecting/lists/${encodeURIComponent(listId)}`, this.options(token),
      )),
      map(res => res.data),
    );
  }

  /** POST /prospecting/lists/{listId}/save — turn the list into an ICP. */
  saveAsIcp(listId: string, name: string, setDefault: boolean, config: ProspectingListConfig | null): Observable<SaveListResult> {
    const body: any = { name, set_default: setDefault, ...(config ?? {}) };
    return this.authService.token().pipe(
      switchMap(token => this.http.post<{ data: SaveListResult }>(
        `${this.apiUrl}/prospecting/lists/${encodeURIComponent(listId)}/save`, body, this.options(token),
      )),
      map(res => res.data),
    );
  }

  // ─── List leads ────────────────────────────────────────────────────

  /** GET /daily-leads?filter[prospecting_list_id]=X — same paginator as the ICP list. */
  getListLeads(listId: string, perPage = 10, dismissed = false, page = 1): Observable<{ data: PaginatedLeads }> {
    return this.authService.token().pipe(
      switchMap(token => {
        const params = new HttpParams()
          .set('filter[prospecting_list_id]', listId)
          .set('filter[is_dismissed]', dismissed ? '1' : '0')
          .set('sort', '-created_at')
          .set('per_page', perPage.toString())
          .set('page', page.toString());

        return this.http.get<{ data: PaginatedLeads }>(`${this.apiUrl}/daily-leads`, this.options(token, params));
      })
    );
  }

  // ─── CSV export + bulk email unlock ────────────────────────────────

  /** GET /daily-leads/unlock-summary?prospecting_list_id=… (or icp_id) */
  getUnlockSummary(scope: LeadScope): Observable<UnlockSummary> {
    return this.authService.token().pipe(
      switchMap(token => this.http.get<{ data: UnlockSummary }>(
        `${this.apiUrl}/daily-leads/unlock-summary`, this.options(token, this.scopeParams(scope)),
      )),
      map(res => res.data),
    );
  }

  /** POST /daily-leads/unlock-emails — starts a bulk unlock job (202). */
  startUnlockEmails(scope: LeadScope): Observable<{ job_id: string | number; total: number; max_cost: number }> {
    return this.authService.token().pipe(
      switchMap(token => this.http.post<{ data: { job_id: string | number; total: number; max_cost: number } }>(
        `${this.apiUrl}/daily-leads/unlock-emails`, scope, this.options(token),
      )),
      map(res => res.data),
    );
  }

  /** GET /daily-leads/unlock-jobs/{jobId} */
  getUnlockJob(jobId: string | number): Observable<UnlockJob> {
    return this.authService.token().pipe(
      switchMap(token => this.http.get<{ data: UnlockJob }>(
        `${this.apiUrl}/daily-leads/unlock-jobs/${jobId}`, this.options(token),
      )),
      map(res => res.data),
    );
  }

  /** GET /daily-leads/export?prospecting_list_id=… (or icp_id) — CSV blob. */
  exportCsv(scope: LeadScope, includeDismissed = false, columns?: string[]): Observable<Blob> {
    let params = this.scopeParams(scope).set('include_dismissed', includeDismissed ? '1' : '0');
    if (columns?.length) {
      params = params.set('columns', columns.join(','));
    }
    return this.authService.token().pipe(
      switchMap(token => this.http.get(`${this.apiUrl}/daily-leads/export`, {
        headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` }),
        params,
        responseType: 'blob',
      }))
    );
  }

  // ─── Lead exports (Excel) ──────────────────────────────────────────

  /** GET /lead-exports */
  listLeadExports(page = 1, perPage = 15): Observable<LeadExportPage> {
    return this.authService.token().pipe(
      switchMap(token => this.http.get<{ data: LeadExport[]; metadata?: any }>(
        `${this.apiUrl}/lead-exports`,
        this.options(token, new HttpParams().set('page', String(page)).set('per_page', String(perPage))),
      )),
      map(res => ({ data: res.data ?? [], metadata: res.metadata ?? {} })),
    );
  }

  /** GET /lead-exports/{id}/status */
  getLeadExport(id: number): Observable<LeadExport> {
    return this.authService.token().pipe(
      switchMap(token => this.http.get<{ data: LeadExport }>(
        `${this.apiUrl}/lead-exports/${id}/status`, this.options(token),
      )),
      map(res => res.data),
    );
  }

  /** POST /lead-exports/{id}/cancel */
  cancelLeadExport(id: number): Observable<LeadExport> {
    return this.authService.token().pipe(
      switchMap(token => this.http.post<{ data: LeadExport }>(
        `${this.apiUrl}/lead-exports/${id}/cancel`, {}, this.options(token),
      )),
      map(res => res.data),
    );
  }

  /** POST /lead-exports/{id}/continue — resume an export that ran out of credits (new part). */
  continueLeadExport(id: number, includeEmails: boolean): Observable<{ export: LeadExport; credits_to_start: number; previous: LeadExport }> {
    return this.authService.token().pipe(
      switchMap(token => this.http.post<{ data: { export: LeadExport; credits_to_start: number; previous: LeadExport } }>(
        `${this.apiUrl}/lead-exports/${id}/continue`, { include_emails: includeEmails }, this.options(token),
      )),
      map(res => res.data),
    );
  }

  /** GET /lead-exports/{id}/download — .xlsx blob. */
  downloadLeadExport(id: number): Observable<Blob> {
    return this.authService.token().pipe(
      switchMap(token => this.http.get(`${this.apiUrl}/lead-exports/${id}/download`, {
        headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` }),
        responseType: 'blob',
      }))
    );
  }
}
